import { useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { ID_ADM } from '@praticheenergia/domain'
import { salvaElenchi, type Elenchi } from './elenchi.api'

/** Etichette dello XSD (tipologiaVenditaType, provenienzaType). */
const TIPOLOGIE_N: Record<number, string> = {
  1: 'Altri operatori del mercato',
  2: 'Acquirenti per uso proprio',
  3: 'Soggetti UE/extra UE',
  4: 'Reselling',
}
const PROVENIENZE_T: Record<number, string> = {
  1: 'UE',
  2: 'Extra-UE',
  3: 'Utente della distribuzione',
  4: 'Mercato elettrico',
  5: 'Fornitore prodotti energetici',
}

interface Riga {
  codice: string
  id: string
  kwh: string
}

interface Props {
  readonly dichiarazioneId: number
  readonly elenchi: Elenchi
}

function Sezione({
  sigla,
  titolo,
  etichettaCodice,
  codici,
  righe,
  onChange,
}: {
  sigla: 'N' | 'T'
  titolo: string
  etichettaCodice: string
  codici: Record<number, string>
  righe: Riga[]
  onChange: (r: Riga[]) => void
}) {
  const modifica = (i: number, campo: keyof Riga, v: string) =>
    onChange(righe.map((r, j) => (j === i ? { ...r, [campo]: v } : r)))
  return (
    <Box>
      <Typography variant="subtitle2">{titolo}</Typography>
      {righe.map((r, i) => (
        <Stack key={i} direction="row" spacing={1} sx={{ my: 1 }}>
          <TextField
            select
            size="small"
            label={`${sigla} riga ${i + 1}, ${etichettaCodice}`}
            slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
            value={r.codice}
            onChange={(e) => modifica(i, 'codice', e.target.value)}
          >
            {Object.entries(codici).map(([k, v]) => (
              <option key={k} value={k}>
                {k} — {v}
              </option>
            ))}
          </TextField>
          <TextField size="small" label={`${sigla} riga ${i + 1}, Id`} value={r.id} onChange={(e) => modifica(i, 'id', e.target.value)} />
          <TextField size="small" label={`${sigla} riga ${i + 1}, kWh`} value={r.kwh} onChange={(e) => modifica(i, 'kwh', e.target.value)} />
          <Button size="small" color="error" onClick={() => onChange(righe.filter((_, j) => j !== i))}>
            Rimuovi
          </Button>
        </Stack>
      ))}
      <Button size="small" onClick={() => onChange([...righe, { codice: '1', id: '', kwh: '' }])}>
        Aggiungi {sigla === 'N' ? 'vendita' : 'provenienza'} ({sigla})
      </Button>
    </Box>
  )
}

/** Controlli dello XSD con messaggi per riga; lo schema li ripete in salvataggio. */
function errori(sigla: string, righe: Riga[]): string[] {
  return righe.flatMap((r, i) => {
    const id = r.id.trim().toUpperCase()
    if (!ID_ADM.test(id)) return [`${sigla} riga ${i + 1}: Id «${r.id}» non valido (codice ditta, codice fiscale, partita IVA o sigla di stato)`]
    if (!/^\d{1,13}$/.test(r.kwh.trim())) return [`${sigla} riga ${i + 1}: kWh «${r.kwh}» non validi (intero)`]
    return []
  })
}

export function EditorElenchi({ dichiarazioneId, elenchi }: Props) {
  const queryClient = useQueryClient()
  const [n, setN] = useState<Riga[]>((elenchi.N ?? []).map((x) => ({ codice: String(x.tipologia), id: x.id, kwh: x.kwh })))
  const [t, setT] = useState<Riga[]>((elenchi.T ?? []).map((x) => ({ codice: String(x.provenienza), id: x.id, kwh: x.kwh })))
  const [problemi, setProblemi] = useState<string[]>([])
  const [salvato, setSalvato] = useState(false)

  async function suSalva() {
    setSalvato(false)
    const trovati = [...errori('N', n), ...errori('T', t)]
    setProblemi(trovati)
    if (trovati.length > 0) return
    const pulisci = (r: Riga) => ({ id: r.id.trim().toUpperCase(), kwh: r.kwh.trim() })
    try {
      await salvaElenchi(dichiarazioneId, {
        ...elenchi,
        N: n.map((r) => ({ tipologia: Number(r.codice), ...pulisci(r) })),
        T: t.map((r) => ({ provenienza: Number(r.codice), ...pulisci(r) })),
      })
      // Il calcolo in anteprima si rifa' con gli elenchi nuovi.
      await queryClient.invalidateQueries({ queryKey: ['dichiarazione'] })
      setSalvato(true)
    } catch (e) {
      setProblemi([e instanceof Error ? e.message : 'errore imprevisto'])
    }
  }

  return (
    <Stack spacing={2}>
      {problemi.length > 0 && (
        <Alert severity="error" role="alert">
          {problemi.map((p) => (
            <div key={p}>{p}</div>
          ))}
        </Alert>
      )}
      {salvato && <Alert severity="info">Quadri N e T salvati</Alert>}
      <Sezione sigla="N" titolo="Energia venduta ad altri soggetti (quadro N)" etichettaCodice="tipologia" codici={TIPOLOGIE_N} righe={n} onChange={setN} />
      <Sezione sigla="T" titolo="Provenienza dell energia (quadro T)" etichettaCodice="provenienza" codici={PROVENIENZE_T} righe={t} onChange={setT} />
      <Box>
        <Button variant="outlined" onClick={() => void suSalva()}>
          Salva N e T
        </Button>
      </Box>
    </Stack>
  )
}
